/**
 * Erros de domínio da integração Memed no frontend.
 */

export const MEMED_ERROR_CODES = {
  DISABLED: 'MEMED_DISABLED',
  UNAVAILABLE: 'MEMED_UNAVAILABLE',
  PROFILE_INCOMPLETE: 'MEMED_PRESCRIBER_PROFILE_INCOMPLETE',
  SESSION_FAILED: 'MEMED_SESSION_CREATION_FAILED',
  SESSION_INVALID: 'MEMED_SESSION_INVALID_RESPONSE',
  SCRIPT_LOAD_FAILED: 'MEMED_SCRIPT_LOAD_FAILED',
  WIDGET_INIT_FAILED: 'MEMED_WIDGET_INIT_FAILED',
  UNAUTHORIZED: 'MEMED_UNAUTHORIZED_LOCAL',
  FORBIDDEN: 'MEMED_FORBIDDEN_LOCAL',
} as const;

export type MemedFrontendErrorCode =
  (typeof MEMED_ERROR_CODES)[keyof typeof MEMED_ERROR_CODES] | string;

export type MemedErrorKind =
  | 'disabled'
  | 'unavailable'
  | 'profile_incomplete'
  | 'auth'
  | 'forbidden'
  | 'technical'
  | 'unknown';

export class MemedFrontendError extends Error {
  readonly code: MemedFrontendErrorCode;
  readonly kind: MemedErrorKind;
  readonly status?: number;
  readonly missingFields?: string[];
  readonly allowLegacyFallback: boolean;

  constructor(params: {
    code: MemedFrontendErrorCode;
    message: string;
    kind: MemedErrorKind;
    status?: number;
    missingFields?: string[];
    allowLegacyFallback?: boolean;
  }) {
    super(params.message);
    this.name = 'MemedFrontendError';
    this.code = params.code;
    this.kind = params.kind;
    this.status = params.status;
    this.missingFields = params.missingFields;
    this.allowLegacyFallback =
      params.allowLegacyFallback ??
      (params.kind === 'technical' || params.kind === 'disabled');
  }
}

/** Mensagem amigável p/ homologação Memed fora do ar (503). */
export const MEMED_HOMOLOGATION_UNAVAILABLE_MESSAGE =
  'A Memed (homologação) está indisponível no momento. Em dias úteis funciona das 6h às 0h (horário de Brasília); fins de semana permanece fora. Tente novamente no horário de disponibilidade.';

export const MEMED_SIGNATURE_REQUIRED_MESSAGE =
  'Assine na Memed com certificado em nuvem (Bird ID, VIDAS ou A3 cloud). O cadastro de .pfx no Dr House não assina a receita. O documento não foi gravado.';

export const MEMED_IDENTITY_MISMATCH_MESSAGE =
  'CPF/CRM da Memed não batem com o perfil. Confira a identidade Memed: o CPF do perfil (só dígitos) deve ser o mesmo do Bird ID.';

export const MEMED_SESSION_AUTH_MESSAGE =
  'Não foi possível criar a sessão Memed. Verifique as credenciais da integração Memed no servidor (MEMED_API_KEY, MEMED_SECRET_KEY e MEMED_API_URL) e se o médico está habilitado na Memed. Isso não é o certificado Bird ID.';

export function classifyMemedApiFailure(params: {
  status?: number;
  code?: string;
  message?: string;
  missingFields?: string[];
}): MemedFrontendError {
  const { status, code, message, missingFields } = params;

  if (status === 401 || code === 'UNAUTHORIZED') {
    return new MemedFrontendError({
      code: MEMED_ERROR_CODES.UNAUTHORIZED,
      message: MEMED_SESSION_AUTH_MESSAGE,
      kind: 'auth',
      status,
      allowLegacyFallback: false,
    });
  }

  if (status === 403) {
    const msg = String(message || '');
    const signatureLike = /Assine na Memed|não assina a receita|assinatura digital ausente/i.test(msg);
    return new MemedFrontendError({
      code: signatureLike ? 'MEMED_UNAUTHORIZED' : MEMED_ERROR_CODES.FORBIDDEN,
      message: signatureLike
        ? MEMED_SIGNATURE_REQUIRED_MESSAGE
        : MEMED_SESSION_AUTH_MESSAGE,
      kind: 'forbidden',
      status,
      allowLegacyFallback: false,
    });
  }

  if (status === 400 || code === 'MEMED_BAD_REQUEST' || code === 'MEMED_PRESCRIBER_SYNC') {
    return new MemedFrontendError({
      code: code || 'MEMED_BAD_REQUEST',
      message: MEMED_IDENTITY_MISMATCH_MESSAGE,
      kind: 'profile_incomplete',
      status,
      missingFields,
      allowLegacyFallback: false,
    });
  }

  if (code === MEMED_ERROR_CODES.DISABLED) {
    return new MemedFrontendError({
      code: MEMED_ERROR_CODES.DISABLED,
      message: 'Prescrição digital temporariamente desativada.',
      kind: 'disabled',
      status,
      allowLegacyFallback: true,
    });
  }

  // Indisponibilidade da API Memed (ex.: homologação fora do ar) — não cai no editor legado.
  if (code === MEMED_ERROR_CODES.UNAVAILABLE || status === 503) {
    return new MemedFrontendError({
      code: MEMED_ERROR_CODES.UNAVAILABLE,
      message: MEMED_HOMOLOGATION_UNAVAILABLE_MESSAGE,
      kind: 'unavailable',
      status: status ?? 503,
      allowLegacyFallback: false,
    });
  }

  if (code === MEMED_ERROR_CODES.PROFILE_INCOMPLETE || status === 422) {
    const fieldsSuffix =
      missingFields && missingFields.length
        ? ` Verifique: ${missingFields.join(', ')}.`
        : '';
    const base =
      message ||
      'Seu cadastro médico precisa ser completado antes de usar a prescrição digital.';
    const withFields =
      fieldsSuffix && !base.toLowerCase().includes('verifique')
        ? `${base}${fieldsSuffix}`
        : base;
    return new MemedFrontendError({
      code: MEMED_ERROR_CODES.PROFILE_INCOMPLETE,
      message: `${withFields} Complete em Configurações.`,
      kind: 'profile_incomplete',
      status,
      missingFields,
      allowLegacyFallback: false,
    });
  }

  return new MemedFrontendError({
    code: code || MEMED_ERROR_CODES.SESSION_FAILED,
    message:
      message ||
      'Não foi possível iniciar a prescrição digital agora.',
    kind: 'technical',
    status,
    allowLegacyFallback: true,
  });
}
